import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../../lib/supabase';
import EditableTable from './EditableTable';

/**
 * CRUD on `public.servers`.
 * super_admin sees and edits the global multi-server registry.
 */
const ServersTab = ({ servers, onChange, loading }) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);

  const columns = [
    {
      key: 'id',
      label: t('superadmin.businessRules.servers.id', 'ID'),
      type: 'text',
      width: '120px',
      readonly: true, // PK is immutable after insert
    },
    {
      key: 'name',
      label: t('superadmin.businessRules.servers.name', 'Name'),
      type: 'text',
    },
    {
      key: 'color',
      label: t('superadmin.businessRules.servers.color', 'Color'),
      type: 'color',
      width: '220px',
    },
    {
      key: 'is_active',
      label: t('superadmin.businessRules.servers.isActive', 'Active'),
      type: 'boolean',
      width: '100px',
    },
    // Note: `advanced_bonus_active` is intentionally NOT exposed here.
    // It depends on per-agency results, not server-level config — managed in agency view (Phase B/C).
  ];

  const handleSave = useCallback(
    async (row, patch) => {
      setError(null);
      const { error: dbError } = await supabase
        .from('servers')
        .update(patch)
        .eq('id', row.id);
      if (dbError) {
        setError(dbError.message);
        throw dbError;
      }
      await onChange();
    },
    [onChange]
  );

  const handleDelete = useCallback(
    async (row) => {
      setError(null);
      const { error: dbError } = await supabase
        .from('servers')
        .delete()
        .eq('id', row.id);
      if (dbError) {
        setError(dbError.message);
        throw dbError;
      }
      await onChange();
    },
    [onChange]
  );

  const handleAdd = useCallback(async () => {
    setError(null);
    const id = window.prompt(
      t(
        'superadmin.businessRules.servers.promptId',
        'New server ID (e.g. AS+, LATAM):'
      )
    );
    if (!id) return;
    const trimmed = id.trim();
    if (!trimmed) return;
    const { error: dbError } = await supabase.from('servers').insert({
      id: trimmed,
      name: trimmed,
      color: '#94a3b8',
      is_active: true,
      advanced_bonus_active: false,
    });
    if (dbError) {
      setError(dbError.message);
      window.alert(dbError.message);
      return;
    }
    await onChange();
  }, [onChange, t]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {t(
          'superadmin.businessRules.servers.intro',
          'Global server registry. The selector above is fed from this list. ID is the primary key — deleting a server cascades to all server-scoped config rows.'
        )}
      </p>
      {error && (
        <div className="rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}
      <EditableTable
        columns={columns}
        rows={servers}
        loading={loading}
        onRowSave={handleSave}
        onRowDelete={handleDelete}
        onAddRow={handleAdd}
        addRowLabel={t('superadmin.businessRules.servers.add', 'Add server')}
        emptyMessage={t(
          'superadmin.businessRules.servers.empty',
          'No servers configured.'
        )}
      />
    </div>
  );
};

export default ServersTab;
